import { useState, useRef, useEffect } from "react";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { toast } from 'react-toastify';
import axios from "axios";
import { Upload, Image as ImageIcon, X, Check, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImageUpload = ({ 
  imageType, 
  currentImageUrl = "", 
  onImageUploaded, 
  label = "Upload Image",
  description = "Drag and drop an image or click to browse",
  className = ""
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentImageUrl);
  const fileInputRef = useRef(null);
  const hasUploadedRef = useRef(false);

  // Only sync previewUrl with currentImageUrl if we haven't uploaded yet
  useEffect(() => {
    if (!hasUploadedRef.current) {
      console.log('🔵 ImageUpload - Initial sync with currentImageUrl:', currentImageUrl);
      setPreviewUrl(currentImageUrl);
    }
  }, [currentImageUrl]);

  // Get image guidelines based on type
  const getImageGuidelines = (type) => {
    const guidelines = {
      hero: { size: "1920x1080px", ratio: "16:9", desc: "Main hero image/video" },
      pillar: { size: "800x600px", ratio: "4:3", desc: "Pillar card image" },
      testimonial: { size: "400x400px", ratio: "1:1", desc: "Profile photo (square)" },
      feature: { size: "200x200px", ratio: "1:1", desc: "Feature icon (square)" }
    };
    return guidelines[type] || { size: "Auto", ratio: "Auto", desc: "Image" };
  };

  const guidelines = getImageGuidelines(imageType);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', imageType);

      console.log('🔵 Uploading image:', { imageType, fileName: file.name });

      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('🔵 Upload response:', response.data);

      if (response.data.success) {
        // Check if the image URL is already absolute (starts with http:// or https://)
        const imageUrl = response.data.image_url;
        const fullImageUrl = imageUrl.startsWith('http://') || imageUrl.startsWith('https://') 
          ? imageUrl 
          : `${BACKEND_URL}${imageUrl}`;
        
        console.log('🔵 Setting preview URL:', fullImageUrl);
        console.log('🔵 Calling onImageUploaded with:', fullImageUrl);
        
        // Mark that we've uploaded, so we don't reset from parent
        hasUploadedRef.current = true;
        
        setPreviewUrl(fullImageUrl);
        onImageUploaded(fullImageUrl);
        
        toast.success(`Image uploaded successfully! ${response.data.file_size || ''}`);
      } else {
        console.error('❌ Upload failed: success=false');
        toast.error("Upload failed");
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      console.error('❌ Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || "Upload failed");
      // Reset preview on error
      setPreviewUrl(currentImageUrl);
    } finally {
      setIsUploading(false);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const clearImage = () => {
    setPreviewUrl("");
    onImageUploaded("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex justify-between items-center">
        <label className="block text-sm font-medium text-burgundy">
          {label}
        </label>
        <div className="text-xs text-burgundy/60">
          Recommended: {guidelines.size} ({guidelines.ratio})
        </div>
      </div>

      {/* Upload Area */}
      <Card 
        className={`relative border-2 border-dashed transition-all duration-200 ${
          isDragging 
            ? 'border-gold bg-gold/10' 
            : 'border-burgundy/30 hover:border-burgundy/50'
        } ${previewUrl ? 'border-solid border-burgundy/20' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <CardContent className="p-6">
          {previewUrl ? (
            // Image Preview
            <div className="relative">
              <img
                src={previewUrl}
                alt="Preview"
                className="w-full h-48 object-cover rounded-lg"
              />
              <div className="absolute top-2 right-2 flex space-x-2">
                {isUploading && (
                  <div className="bg-white/90 rounded-full p-2">
                    <Loader2 className="h-4 w-4 animate-spin text-burgundy" />
                  </div>
                )}
                {!isUploading && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-white/90 hover:bg-white"
                      onClick={handleBrowseClick}
                    >
                      <Upload className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-white/90 hover:bg-white"
                      onClick={clearImage}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
              <div className="absolute bottom-2 left-2 bg-white/90 rounded px-2 py-1 text-xs text-burgundy">
                {guidelines.desc}
              </div>
            </div>
          ) : (
            // Upload Prompt
            <div 
              className="text-center py-8 cursor-pointer"
              onClick={handleBrowseClick}
            >
              <div className="mx-auto mb-4">
                {isUploading ? (
                  <Loader2 className="h-12 w-12 text-burgundy animate-spin mx-auto" />
                ) : (
                  <ImageIcon className="h-12 w-12 text-burgundy/50 mx-auto" />
                )}
              </div>
              
              <p className="text-burgundy font-medium mb-2">
                {isUploading ? 'Processing image...' : description}
              </p>
              
              <div className="text-xs text-burgundy/60 space-y-1">
                <p>• Supports JPG, PNG, WebP</p>
                <p>• Max file size: 5MB</p>
                <p>• Auto-resized to {guidelines.size}</p>
              </div>

              <Button
                type="button"
                variant="outline"
                className="mt-4 border-burgundy/30 text-burgundy hover:bg-burgundy/5"
                disabled={isUploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                {isUploading ? 'Uploading...' : 'Browse Files'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Guidelines */}
      <div className="text-xs text-burgundy/50 bg-cream/50 p-3 rounded-lg">
        <p className="font-medium text-burgundy/70 mb-1">💡 Image Tips:</p>
        <p>• {guidelines.desc} works best at {guidelines.size}</p>
        <p>• Images are automatically cropped and optimized</p>
        <p>• High resolution images (1200px+) look sharper</p>
      </div>
    </div>
  );
};

export default ImageUpload;